import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { markAllNotificationsRead, markNotificationRead, type InAppNotification } from '@/api/notifications';
import { useAuthStore } from '@/stores/authStore';
import { useMyNotifications } from '@/hooks/useMyNotifications';
import { hubQueryKeys } from '@/lib/hubQueryClient';

function BellIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
      />
    </svg>
  );
}

export function NotificationBell({ className = '' }: { className?: string }) {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: notifications = [] } = useMyNotifications();

  const unread = notifications.filter((n) => !n.isRead);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!user) return null;

  const openItem = async (n: InAppNotification) => {
    if (!n.isRead) {
      await markNotificationRead(n.id);
      void queryClient.invalidateQueries({ queryKey: hubQueryKeys.notifications(user.id) });
    }
    setOpen(false);
  };

  const markAll = async () => {
    const ids = unread.map((n) => n.id);
    await markAllNotificationsRead(user.id, ids);
    void queryClient.invalidateQueries({ queryKey: hubQueryKeys.notifications(user.id) });
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
        aria-label={unread.length ? `${unread.length} unread notifications` : 'Notifications'}
        aria-expanded={open}
      >
        <BellIcon />
        {unread.length > 0 ? (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread.length > 9 ? '9+' : unread.length}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">Notifications</p>
            {unread.length > 0 ? (
              <button
                type="button"
                onClick={() => void markAll()}
                className="text-xs font-semibold text-brand-700 dark:text-brand-400"
              >
                Mark all read
              </button>
            ) : null}
          </div>
          <ul className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <li className="px-4 py-6 text-center text-sm text-slate-500">No notifications yet.</li>
            ) : (
              notifications.map((n) => (
                <li key={n.id} className="border-b border-slate-50 last:border-0 dark:border-slate-800">
                  {n.link ? (
                    <Link
                      to={n.link}
                      onClick={() => void openItem(n)}
                      className={`block px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800 ${
                        !n.isRead ? 'bg-brand-50/50 dark:bg-brand-950/20' : ''
                      }`}
                    >
                      <NotificationRow n={n} />
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void openItem(n)}
                      className={`block w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800 ${
                        !n.isRead ? 'bg-brand-50/50 dark:bg-brand-950/20' : ''
                      }`}
                    >
                      <NotificationRow n={n} />
                    </button>
                  )}
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function NotificationRow({ n }: { n: InAppNotification }) {
  return (
    <>
      <p className="text-sm font-semibold text-slate-900 dark:text-white">{n.title}</p>
      {n.body ? <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">{n.body}</p> : null}
    </>
  );
}
